import Check from "@mui/icons-material/Check";
import CompareArrowsIcon from "@mui/icons-material/CompareArrows";
import UploadFile from "@mui/icons-material/UploadFile";
import { Box, Button, Chip, Stack, Tooltip, useMediaQuery, useTheme } from "@mui/material";
import { GridRenderCellParams, useGridApiRef } from "@mui/x-data-grid-premium";
import { mdHeight, SubNavAppBar, xsHeight } from "components/AppBar";
import { ExportDataButton } from "components/ExportDataButton";
import { OmnisearchDataGrid } from "components/OmnisearchDataGrid";
import { tabsOffset } from "components/TabLinks";
import { STATE_NAMES } from "encamp-shared/src/constants/states";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { getUtcDisplayDate } from "encamp-shared/src/utils/date";
import { ProcessedManifestsQuery } from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useLocalStorageState } from "hooks/useLocalStorageState";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useQueueManifestDocumentParse } from "hooks/useQueueManifestDocumentParse";
import { useSelectedIdInUrl } from "hooks/useSelectedIdInUrl";
import { DateTime } from "luxon";
import { useCallback, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { DAYS_IN_TRANSIT_TOOLTIP_TEXT, getDaysInTransitInfo } from "util/manifest";

import { YearAutocompletePicker } from "../ManifestLineItems/YearAutocompletePIcker";
import { PROCESSED_MANIFESTS_QUERY } from "./api";
import { DiscrepancyReviewDialog } from "./DiscrepancyReviewDialog";
import { ManifestDetails } from "./ManifestDetails";
import { ManifestUploadDialog } from "./ManifestUploadDialog";

export type Row = ProcessedManifestsQuery["processedManifests"]["items"][number];

export function Manifests() {
  const { tenantId } = useParams<{ tenantId: string }>();
  const persistenceKey = "waste-manifests";
  const minWidth = 120;
  const theme = useTheme();
  const { featureFlags } = useFeatureFlags();
  const { isStaff } = useCurrentUser();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isLeftNavV2 = featureFlags[FeatureFlagKey.LeftNavV2];
  const showDiscrepancyCounts = isStaff && featureFlags[FeatureFlagKey.WasteManifestDiscrepancies];
  const { canQueueManifestParse } = useQueueManifestDocumentParse();
  const [manifestUploadDialogOpen, setManifestUploadDialogOpen] = useState(false);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [showBulkReviewDialog, setShowBulkReviewDialog] = useState(false);

  const apiRef = useGridApiRef();

  const renderDaysInTransit = useCallback(
    (row: Row): React.ReactNode => {
      const result = getDaysInTransitInfo(row.eManifestDaysInTransit, row.receivedDate, theme);

      if (!result) {
        return "";
      }

      // Always show with color coding based on thresholds
      // Use italic + tooltip if not yet received
      if (!row.receivedDate) {
        return (
          <Tooltip title={result.tooltipText} placement="right" enterDelay={500}>
            <Box component="i" sx={{ color: result.color }}>
              {result.display}
            </Box>
          </Tooltip>
        );
      }

      // Received - show with color but not italic
      return <Box sx={{ color: result.color }}>{result.display}</Box>;
    },
    [theme]
  );

  const { selectedId, pushIdToUrl, popIdFromUrl } = useSelectedIdInUrl("processedManifestId");

  const [shippedDateYear, setShippedDateYear] = useLocalStorageState<number>(
    "waste-manifest-shipped-date-year",
    {
      defaultValue: DateTime.now().year,
    }
  );

  const filename = useMemo(() => `waste-manifests-${shippedDateYear}`, [shippedDateYear]);

  const defaultSearch = useMemo(
    () => `tenantId:${tenantId} shippedDate:${shippedDateYear}`,
    [tenantId, shippedDateYear]
  );
  useBreadcrumb([
    {
      label: "Waste",
      to: tenantId ? `/o/${tenantId}/waste` : undefined,
    },
    {
      label: "Manifests",
    },
  ]);

  const columns: OmnisearchGridColDef<Row>[] = useMemo(
    () => [
      {
        field: "manifestTrackingNumber",
        headerName: "Manifest #",
        minWidth,
        flex: 1,
      },
      {
        field: "epaId",
        headerName: "EPA ID",
        minWidth,
        flex: 1,
      },
      {
        field: "facility.name",
        headerName: "Facility",
        filterKeyType: "wasteFacility",
        minWidth,
        flex: 1,
        valueGetter: (_, row) => row.facility?.name ?? "",
      },
      {
        field: "shippedDate",
        headerName: "Ship Date",
        minWidth,
        flex: 1,
        filterKeyType: "date",
        valueFormatter: (value) => {
          return value ? getUtcDisplayDate(value, "med") : null;
        },
      },

      {
        field: "wasteLineCount",
        headerName: "Line Item Count",
        flex: 1,
        minWidth,
      },
      ...(showDiscrepancyCounts
        ? [
            {
              field: "unresolvedDiscrepancyCount" as const,
              headerName: "Unresolved Discrepancies",
              flex: 1,
              minWidth,
              renderHeader: () => (
                <Box fontWeight="medium" sx={{ whiteSpace: "normal", lineHeight: 1.3 }}>
                  Unresolved Discrepancies
                </Box>
              ),
              renderCell: ({ value }: GridRenderCellParams) =>
                value ? (
                  <Chip label={value} size="small" color="warning" variant="outlined" />
                ) : null,
            },
            {
              field: "pendingDiscrepancyCount" as const,
              headerName: "Pending Discrepancies",
              flex: 1,
              minWidth,
              renderHeader: () => (
                <Box fontWeight="medium" sx={{ whiteSpace: "normal", lineHeight: 1.3 }}>
                  Pending Discrepancies
                </Box>
              ),
              renderCell: ({ value }: GridRenderCellParams) =>
                value ? (
                  <Chip label={value} size="small" color="default" variant="outlined" />
                ) : null,
            },
          ]
        : []),
      {
        field: "vendor",
        headerName: "Vendor",
        flex: 1,
      },
      {
        field: "isEManifest",
        headerName: "e‑Manifest",
        flex: 1,
        sortable: true,
        filterKeyType: "boolean",
        renderCell: ({ row }) => {
          if (row.isEManifest) {
            return <Check />;
          }

          return null;
        },
      },
      {
        // Hidden column only used on the backend for filtering, see columnVisibilityModel below
        field: "isCertified",
        headerName: "Certified",
        flex: 1,
        sortable: true,
        filterKeyType: "boolean",
        renderCell: ({ row }) => {
          if (row.isCertified) {
            return <Check />;
          }

          return null;
        },
      },
      {
        field: "receivedDate",
        headerName: "Received by TSDF",
        minWidth,
        flex: 1,
        filterKeyType: "date",
        renderCell: ({ value, row }) => {
          if (value) {
            return getUtcDisplayDate(value, "med");
          }
          // Only show "Pending" for regulated e-Manifests
          if (row.isRegulatedEManifest) {
            return (
              <Box component="i" sx={{ color: theme.palette.text.secondary }}>
                Pending
              </Box>
            );
          }
          return null;
        },
      },

      {
        field: "eManifestDaysInTransit",
        headerName: "Days In Transit",
        minWidth,
        flex: 1,
        filterKeyType: "number",
        renderHeader: () => (
          <Tooltip title={DAYS_IN_TRANSIT_TOOLTIP_TEXT} placement="top-start" enterDelay={500}>
            <Box fontWeight="medium">Days In Transit</Box>
          </Tooltip>
        ),
        renderCell: ({ row }) => renderDaysInTransit(row),
      },
    ],
    [renderDaysInTransit, showDiscrepancyCounts, theme]
  );

  return (
    <Box>
      <SubNavAppBar
        sx={{
          py: 2,
          px: 3,
          borderBottom: "1px solid",
          borderColor: theme.palette.divider,
          ...(!isLeftNavV2 && {
            top: {
              xs: theme.spacing(xsHeight + tabsOffset),
              md: theme.spacing(mdHeight + tabsOffset),
            },
          }),
        }}
      >
        <Stack direction={{ xs: "column", md: "row" }} alignItems="start" spacing={2}>
          <Box sx={{ width: 80 }}>
            <YearAutocompletePicker
              label="Year"
              minYear={2018}
              value={shippedDateYear}
              onChange={setShippedDateYear}
            />
          </Box>
        </Stack>
      </SubNavAppBar>
      <Box py={3}>
        <OmnisearchDataGrid
          apiRef={apiRef}
          persistenceKey={persistenceKey}
          isURLDriven
          columns={columns}
          dataQuery={PROCESSED_MANIFESTS_QUERY}
          getItems={(data) => data.processedManifests.items}
          getCount={(data) => data.processedManifests.count}
          noDataMessage={`No manifests have been added to this organization.`}
          checkboxSelection={showDiscrepancyCounts}
          onSelectedIdsChanged={
            showDiscrepancyCounts ? (ids) => setSelectedIds(ids.map(String)) : undefined
          }
          disableRowSelectionOnClick
          onRowClick={({ row: { id } }) => pushIdToUrl(id)}
          defaultSearch={defaultSearch}
          initialSortModel={[{ field: "shippedDate", sort: "desc" }]}
          rowHeight={"auto"}
          commandButtons={({ search }) => [
            ...(showDiscrepancyCounts && selectedIds.length > 0
              ? [
                  <Button
                    key="review-discrepancies"
                    variant="outlined"
                    color="warning"
                    size="small"
                    startIcon={<CompareArrowsIcon />}
                    onClick={() => setShowBulkReviewDialog(true)}
                  >
                    Review Discrepancies ({selectedIds.length})
                  </Button>,
                ]
              : []),
            ...(canQueueManifestParse
              ? [
                  <Button
                    key="upload-manifest-pdf"
                    variant="outlined"
                    color="warning"
                    startIcon={<UploadFile />}
                    onClick={() => setManifestUploadDialogOpen(true)}
                  >
                    Upload Manifest PDF
                  </Button>,
                ]
              : []),
            <ExportDataButton
              key="export"
              fileName={filename}
              tenantId={tenantId}
              filter={{ search }}
              exportType="wasteManifests"
              iconOnly
            />,
          ]}
          showFilters
          additionalFilterColumns={[
            {
              header: "State",
              key: "states",
              filterKeyType: "enumAutocomplete",
              enumValues: Object.keys(STATE_NAMES),
              enumPresentationFunction: (value: string) => {
                const name = STATE_NAMES[value as keyof typeof STATE_NAMES];
                return name ? `${name} (${value})` : value;
              },
            },
          ]}
          excludeFilterColumns={["wasteLineCount"]}
          columnVisibilityModel={{
            isCertified: false,
            ...(showDiscrepancyCounts && {
              receivedDate: false,
              eManifestDaysInTransit: false,
            }),
            ...(isMobile && {
              epaId: false,
              wasteLines: false,
              amount: false,
              wasteProfileNumber: false,
              managementMethodCode: false,
              vendor: false,
              eManifest: false,
              vendorManifest: false,
            }),
          }}
        />
      </Box>
      <ManifestDetails
        processedManifestId={selectedId}
        open={!!selectedId}
        onClose={popIdFromUrl}
      />
      {showBulkReviewDialog && selectedIds.length > 0 && (
        <DiscrepancyReviewDialog
          processedManifestIds={selectedIds}
          onClose={() => setShowBulkReviewDialog(false)}
        />
      )}
      {canQueueManifestParse && (
        <ManifestUploadDialog
          open={manifestUploadDialogOpen}
          onClose={() => setManifestUploadDialogOpen(false)}
          tenantId={tenantId ?? ""}
        />
      )}
    </Box>
  );
}
